import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Icon } from '@iconify/react';
import CONFIG from '../../config';
import LoadingSpinner from '../common/LoadingSpinner';

const ResultWindow = ({ project }) => {
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterBatch, setFilterBatch] = useState('all');

    useEffect(() => {
        if (project) {
            fetchResults();
        }
    }, [project]);

    const fetchResults = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/candidates/results/${project._id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setResults(resp.data);
        } catch (err) {
            console.error('Failed to fetch results:', err);
        } finally {
            setLoading(false);
        }
    };

    const batches = ['all', ...new Set(results.map(r => r.batch_id).filter(Boolean))];

    const filteredResults = results.filter(r => {
        const dateStr = new Date(r.completed_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }).toLowerCase();
        const matchesSearch =
            r.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.unique_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            r.chest_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            dateStr.includes(searchTerm.toLowerCase());

        const matchesBatch = filterBatch === 'all' || r.batch_id === filterBatch;

        return matchesSearch && matchesBatch;
    });


    return (
        <div className="ro-results-container" style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {/* Header / Filter Bar */}
            <div className="glass-card" style={{ padding: '24px', display: 'flex', flexWrap: 'wrap', gap: '20px', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ padding: '10px', background: 'rgba(128,0,0,0.1)', color: 'var(--primary)', borderRadius: '12px' }}>
                        <Icon icon="ion:ribbon-outline" width="24" />
                    </div>
                    <div>
                        <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 900 }}>RESULT WINDOW</h2>
                        <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', fontWeight: 600 }}>CANDIDATES WHO COMPLETED TRACKER STAGE</p>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', flex: 1, justifyContent: 'flex-end', minWidth: '300px' }}>
                    <div style={{ position: 'relative', flex: 1, maxWidth: '400px' }}>
                        <Icon icon="ion:search-outline" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
                        <input
                            type="text"
                            placeholder="Search by ID, Chest #, or Name..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '12px 12px 12px 40px',
                                borderRadius: '12px',
                                border: '1px solid var(--border-light)',
                                background: '#f8fafc',
                                outline: 'none',
                                fontSize: '14px',
                                fontWeight: 600
                            }}
                        />
                    </div>

                    <select
                        value={filterBatch}
                        onChange={(e) => setFilterBatch(e.target.value)}
                        style={{
                            padding: '12px 20px',
                            borderRadius: '12px',
                            border: '1px solid var(--border-light)',
                            background: 'white',
                            color: 'var(--text-dark)',
                            fontWeight: 700,
                            cursor: 'pointer',
                            outline: 'none'
                        }}
                    >
                        <option value="all">All Batches</option>
                        {batches.filter(b => b !== 'all').map(b => (
                            <option key={b} value={b}>Batch {b}</option>
                        ))}
                    </select>

                    <button
                        onClick={fetchResults}
                        className="btn-refresh"
                        style={{
                            padding: '12px',
                            borderRadius: '12px',
                            border: '1px solid var(--border-light)',
                            background: 'white',
                            color: 'var(--primary)',
                            cursor: 'pointer'
                        }}
                    >
                        <Icon icon="ion:refresh-outline" width="20" />
                    </button>
                </div>
            </div>

            {/* Content Table */}
            <div className="ro-table-container glass-card" style={{ overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                    <table className="ro-table">
                        <thead>
                            <tr>
                                <th>Unique ID</th>
                                <th>Chest #</th>
                                <th>Full Name</th>
                                <th>Batch</th>
                                <th>Laps Completed</th>
                                <th>Result</th>
                                <th>Completion Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '100px' }}>
                                        <LoadingSpinner message="Fetching Results..." />
                                    </td>
                                </tr>
                            ) : filteredResults.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center', padding: '100px' }}>
                                        <div style={{ fontSize: '48px', color: '#e2e8f0' }}>
                                            <Icon icon="ion:document-text-outline" />
                                        </div>
                                        <div style={{ marginTop: '16px', fontWeight: 800, color: '#94a3b8' }}>NO RESULTS FOUND</div>
                                        <p style={{ color: '#cbd5e1', fontSize: '13px' }}>Try adjusting your filters or search terms.</p>
                                    </td>
                                </tr>
                            ) : filteredResults.map(cand => (
                                <tr key={cand.id}>
                                    <td style={{ fontWeight: 800, color: 'var(--text-muted)', fontSize: '13px' }}>{cand.unique_id}</td>
                                    <td style={{ fontWeight: 900, color: 'var(--primary)' }}>{cand.chest_number}</td>
                                    <td style={{ fontWeight: 700 }}>{cand.full_name}</td>
                                    <td>
                                        <span style={{ padding: '4px 8px', borderRadius: '6px', background: '#f1f5f9', fontWeight: 700, fontSize: '12px' }}>
                                            {cand.batch_id || 'N/A'}
                                        </span>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 800 }}>
                                            <span style={{ color: cand.laps >= 5 ? '#10b981' : '#f59e0b' }}>{cand.laps}</span>
                                            <div style={{ width: '60px', height: '6px', background: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
                                                <div style={{
                                                    width: `${Math.min(100, (cand.laps / 5) * 100)}%`,
                                                    height: '100%',
                                                    background: cand.laps >= 5 ? '#10b981' : '#f59e0b'
                                                }}></div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span style={{
                                            padding: '6px 12px',
                                            borderRadius: '20px',
                                            fontSize: '11px',
                                            fontWeight: 900,
                                            background: cand.result === 'PASS' ? '#dcfce7' : '#fee2e2',
                                            color: cand.result === 'PASS' ? '#166534' : '#991b1b',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '4px'
                                        }}>
                                            <Icon icon={cand.result === 'PASS' ? "ion:checkmark-circle" : "ion:close-circle"} />
                                            {cand.result}
                                        </span>
                                    </td>
                                    <td style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-muted)' }}>
                                        {new Date(cand.completed_at).toLocaleString('en-IN', {
                                            day: '2-digit',
                                            month: 'short',
                                            year: 'numeric'
                                        })}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <style sx>{`
                .ro-table { width: 100%; border-collapse: collapse; }
                .ro-table th { text-align: left; padding: 16px 20px; font-size: 11px; font-weight: 800; color: #64748b; text-transform: uppercase; border-bottom: 2px solid #f1f5f9; background: #f8fafc; }
                .ro-table td { padding: 18px 20px; border-bottom: 1px solid #f1f5f9; font-size: 14px; }
                .ro-table tr:hover { background: #f8fafc; }
                .btn-refresh:active { transform: scale(0.95); }
                .glass-card { background: rgba(255, 255, 255, 0.95); border-radius: 20px; border: 1px solid var(--border-light); box-shadow: 0 10px 30px rgba(0,0,0,0.05); }
            `}</style>
        </div>
    );
};

export default ResultWindow;
